'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import SectionHeading from '@/components/ui/SectionHeading';
import RoadConditionsMap from '@/components/road/RoadConditionsMap';
import RouteCards from '@/components/road/RouteCards';
import type { RoadConditionsData, RoadCamera } from '@/lib/road-conditions';
import AutoLink from '@/components/ui/AutoLink';

export interface RoadConditionsLabels {
  heroTitle?: string;
  heroSubtitle?: string;
  routesTitle?: string;
  camerasTitle?: string;
  camerasSubtitle?: string;
  camerasNoData?: string;
  camerasNoImage?: string;
  tipsTitle?: string;
  tips?: string[];
  colourLabels?: Partial<Record<'green' | 'yellow' | 'amber' | 'red' | 'black', string>>;
  attributionBefore?: string;
  attributionAfter?: string;
  planRoute?: string;
}

const DEFAULT_TIPS = [
  'Studded tyres are mandatory from November to March and strongly recommended October\u2013April.',
  'Drive to conditions, not the speed limit. Black ice is common, especially on bridges and shaded sections.',
  'Keep headlights on at all times \u2014 polar night means minimal daylight from November to January.',
  'Carry an emergency kit: warm blankets, torch, phone charger, snacks. Mobile coverage can be patchy.',
  'Reindeer frequently cross roads, especially at dawn and dusk. Slow down if you see one \u2014 more are likely nearby.',
  'Fill up your tank before long stretches. Petrol stations can be 100km+ apart in Lapland.',
];

export default function RoadConditionsPage({
  initialData,
  labels,
}: {
  initialData?: RoadConditionsData | null;
  labels?: RoadConditionsLabels;
}) {
  const WINTER_TIPS = labels?.tips ?? DEFAULT_TIPS;
  const [cameras, setCameras] = useState<RoadCamera[]>(initialData?.cameras ?? []);
  const [camerasLoading, setCamerasLoading] = useState(!initialData);

  useEffect(() => {
    if (initialData) return;
    fetch('/api/road-conditions')
      .then((res) => res.json())
      .then((d: RoadConditionsData) => setCameras(d.cameras ?? []))
      .catch(() => setCameras([]))
      .finally(() => setCamerasLoading(false));
  }, [initialData]);

  return (
    <main
      className="min-h-screen bg-[#FAFAF5] text-[#1A1A2E]"
    >
      {/* Header */}
      <section className="py-12 px-4 max-w-6xl mx-auto">
        <h1 className="font-sans text-4xl md:text-5xl font-light mb-4">
          {labels?.heroTitle ?? 'Lapland Road Conditions'}
        </h1>
        <p className="text-lg max-w-2xl text-[#5A5A6E]">
          <AutoLink>{labels?.heroSubtitle ?? 'Live colour-coded road map for driving in Finnish Lapland. Updated every 5 minutes from Fintraffic.'}</AutoLink>
        </p>
      </section>

      {/* Map removed - Leaflet tiles caused high LCP (4s+) */}

      {/* Route Summary Cards */}
      <section className="py-12 px-4 max-w-6xl mx-auto">
        <SectionHeading title={labels?.routesTitle ?? 'Key Routes'} />
        <RouteCards colourLabels={labels?.colourLabels} />
      </section>

      {/* Road Cameras Grid */}
      <section className="py-12 px-4 max-w-6xl mx-auto">
        <SectionHeading
          title={labels?.camerasTitle ?? 'Road Cameras'}
          subtitle={labels?.camerasSubtitle ?? 'Live views from Lapland roads'}
        />

        {camerasLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden animate-pulse bg-white shadow-sm"
              >
                <div
                  className="aspect-video bg-[#5A5A6E]/10"
                />
                <div className="p-3">
                  <div
                    className="h-4 w-2/3 rounded bg-[#5A5A6E]/10"
                  />
                </div>
              </div>
            ))}
          </div>
        ) : cameras.length === 0 ? (
          <p className="text-sm text-[#5A5A6E]">
            {labels?.camerasNoData ?? 'No camera data available'}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cameras.slice(0, 6).map((camera) => {
              const imageUrl = camera.presets?.[0]?.imageUrl;
              return (
                <div
                  key={camera.id}
                  className="rounded-xl overflow-hidden bg-white shadow-sm"
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={camera.name}
                      className="w-full aspect-video object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="w-full aspect-video flex items-center justify-center bg-[#5A5A6E]/10"
                    >
                      <span
                        className="text-sm text-[#5A5A6E]"
                      >
                        {labels?.camerasNoImage ?? 'No image available'}
                      </span>
                    </div>
                  )}
                  <div className="p-3">
                    <p
                      className="text-sm font-medium text-[#1A1A2E]"
                    >
                      {camera.name}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Winter Driving Tips - always shown now */}
      <section className="py-12 px-4 max-w-6xl mx-auto">
        <SectionHeading title={labels?.tipsTitle ?? 'Winter Driving in Lapland'} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {WINTER_TIPS.map((tip, i) => (
            <div
              key={i}
              className="rounded-xl p-5 bg-[#F0EDE5] flex gap-4 items-start"
            >
              <span className="shrink-0 flex items-center justify-center h-7 w-7 rounded-full bg-[#9B7B3A] text-[#FAFAF5] text-sm font-bold">
                {i + 1}
              </span>
              <p className="text-sm text-[#1A1A2E] leading-relaxed">{tip}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Attribution */}
      <section
        className="py-8 px-4 max-w-6xl mx-auto border-t border-[#5A5A6E]/10"
      >
        <p className="text-sm text-[#5A5A6E]">
          {labels?.attributionBefore ?? 'Road condition data provided by '}
          <a
            href="https://www.digitraffic.fi/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:opacity-80 transition-opacity text-[#9B7B3A]"
          >
            Fintraffic / Digitraffic
          </a>
          {labels?.attributionAfter ?? '. Licensed under CC BY 4.0.'}
        </p>
        <p
          className="text-sm mt-1 text-[#5A5A6E]"
        >
          {labels?.planRoute ?? 'Plan your route: fintraffic.fi'}
        </p>
      </section>
    </main>
  );
}
